import { useMemo } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";

const HeadInjection = ({ rawHtml }) => {
  let inlineNoscripts = [];
  let inlineScripts = [];
  let inlineStyles = [];

  const headElements = useMemo(() => {
    // Reset your inline elements before processing the new raw HTML.
    inlineNoscripts = [];
    inlineScripts = [];
    inlineStyles = [];

    const parser = new DOMParser();
    // Wrap the raw HTML in a container element for proper parsing.
    const doc = parser.parseFromString(`<div>${rawHtml}</div>`, "text/html");
    const container = doc.body.firstChild;

    // Helper: Create an object with all attributes from a node.
    const getAttributes = (node) => {
      const attrs = {};
      if (node.attributes) {
        Array.from(node.attributes).forEach((attr) => {
          attrs[attr.name] = attr.value;
        });
      }
      return attrs;
    };

    // Process each child node from the container.
    if (!container || !container.childNodes) return [];
    return Array.from(container.childNodes)
      .map((node, index) => {
        const nodeName = node.nodeName.toUpperCase();
        const key = index;
        const commonProps = getAttributes(node);

        switch (nodeName) {
          case "SCRIPT": {
            // If the script has a src attribute, it's an external script.
            if (commonProps.src) {
              return <script key={key} {...commonProps} />;
            } else {
              // If the script has inline content, store it for later use.
              inlineScripts.push(node.textContent);
              return null;
            }
          }
          case "NOSCRIPT": {
            inlineNoscripts.push(node.innerHTML);
            return null;
          }
          case "STYLE": {
            inlineStyles.push(node.textContent);
            return null;
          }
          case "TITLE":
            return <title key={key} {...commonProps} />;
          case "LINK":
            return <link key={key} {...commonProps} />;
          case "META":
            return <meta key={key} {...commonProps} />;
          default:
            // You can choose to return null or handle other node types if needed.
            return null;
        }
      })
      .filter((el) => el !== null); // Remove any null entries.
  }, [rawHtml]);

  return (
    <Helmet
      script={inlineScripts.map((inlineScript) => ({
        type: "text/javascript",
        innerHTML: inlineScript,
      }))}
      noscript={inlineNoscripts.map((noscript) => ({
        innerHTML: noscript,
      }))}
      style={inlineStyles.map((style) => ({
        type: "text/css",
        cssText: style,
      }))}
    >
      {headElements}
    </Helmet>
  );
};

HeadInjection.propTypes = {
  rawHtml: PropTypes.string.isRequired,
};

export default HeadInjection;
